/**
 * PreToolUse Hook Handler — artifact guardrail + DSL guardrail enforcement.
 *
 * Executed before tool use (Edit, Write, Bash, etc.). Checks:
 * 1. Edit/Write tools: file path → artifact reverse lookup → deny if protected
 * 2. Bash tool: apply DSL guardrail rules via GuardrailHooks
 */

import {
  createGuardrailHooksFromRules,
  getBlockingViolations,
  type GuardrailRuleData,
} from "../guardrail-hooks.js";
import {
  resolvePathToArtifacts,
  isProtectedArtifact,
} from "./artifact-resolver.js";
import {
  buildDenyResponse,
  buildAllowResponse,
  type HookOutput,
  type PreToolUseInput,
} from "./hook-protocol.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PreToolUseHandlerConfig {
  /** Navigation index for artifact resolution. */
  navigationIndex: Record<string, unknown>;
  /** DSL guardrail rules for Bash command evaluation. */
  guardrailRules?: GuardrailRuleData;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Tool names that perform file edits. */
const EDIT_TOOLS = new Set(["Edit", "Write", "MultiEdit", "edit", "write"]);

/** Tool names that execute shell commands. */
const BASH_TOOLS = new Set(["Bash", "bash", "Terminal", "terminal"]);

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

/**
 * Create a PreToolUse hook handler.
 */
export function createPreToolUseHandler(config: PreToolUseHandlerConfig) {
  // Pre-compile guardrail hooks if rules provided
  const guardrailHooks = config.guardrailRules
    ? createGuardrailHooksFromRules(config.guardrailRules)
    : null;

  return async function handlePreToolUse(
    input: PreToolUseInput,
  ): Promise<HookOutput> {
    const toolName = input.tool_name ?? "";
    const toolInput = input.tool_input ?? {};

    // --- Edit/Write tool: artifact protection ---
    if (EDIT_TOOLS.has(toolName)) {
      const filePath = toolInput.file_path ?? toolInput.path ?? "";
      if (filePath) {
        const matches = resolvePathToArtifacts(
          filePath,
          config.navigationIndex,
        );

        for (const match of matches) {
          const check = isProtectedArtifact(match);
          if (check.protected) {
            return buildDenyResponse(check.reason);
          }
        }
      }
    }

    // --- Bash tool: DSL guardrail enforcement ---
    if (BASH_TOOLS.has(toolName) && guardrailHooks) {
      const command = toolInput.command ?? "";
      if (command) {
        const hookResult = guardrailHooks.beforeShellExecution({ command });
        if (hookResult.permission === "deny") {
          return buildDenyResponse(
            hookResult.user_message ?? "Command blocked by guardrail",
          );
        }
      }
    }

    // --- Default: allow ---
    return buildAllowResponse();
  };
}
